import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BULLETS = [
  "Blessed is a technology platform that connects donors and recipients — we don't hold your money.",
  "All money moves through Stripe. You also agree to Stripe's Connect Terms.",
  "You must be 18 or older to use Blessed.",
  "Gifts are gifts — they are non-refundable unless the recipient chooses to refund.",
  "You are responsible for any income taxes on money you receive.",
  "We may suspend accounts for fraud, harassment, or other prohibited use.",
  "Disputes are resolved by binding individual arbitration — no class actions.",
];

export default function Terms() {
  const { termsVersion, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [age, setAge] = useState(false);
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    if (!age || !agree) return;
    setSaving(true);
    try {
      await api.post("/users/me/accept-terms", { version: termsVersion, terms_version: termsVersion, age_18_plus: true });
      await refreshUser();
      toast.success("Welcome to Blessed 🎉");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-surface">
      <div className="w-full max-w-md bg-surface-2 rounded-lg border border-border p-7 shadow-xl animate-slide-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-pill grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#1E40AF,#5B21B6)" }}>
            <HeartHandshake size={26} strokeWidth={2.6} />
          </div>
          <div>
            <h1 className="text-xl font-black">A quick agreement</h1>
            <p className="text-xs text-ink-tertiary font-semibold">Terms {termsVersion || "…"}</p>
          </div>
        </div>

        <ul className="text-sm text-ink-tertiary leading-relaxed space-y-2.5 mb-4" data-testid="terms-bullets">
          {BULLETS.map((b, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <button data-testid="terms-read-full" onClick={() => navigate("/terms")} className="text-sm font-bold text-brand-primary underline underline-offset-2 mb-5">
          Read the full Terms & Privacy Policy
        </button>

        <label className="flex items-start gap-3 mb-3 cursor-pointer">
          <Checkbox data-testid="terms-age-checkbox" checked={age} onCheckedChange={setAge} className="mt-0.5" />
          <span className="text-sm font-semibold">I am 18 years of age or older.</span>
        </label>
        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <Checkbox data-testid="terms-agree-checkbox" checked={agree} onCheckedChange={setAgree} className="mt-0.5" />
          <span className="text-sm font-semibold">I agree to the Blessed Terms of Service & Privacy Policy.</span>
        </label>

        <Button
          data-testid="terms-accept-button"
          disabled={!age || !agree || saving}
          onClick={accept}
          className="w-full h-12 rounded-pill font-extrabold bg-brand-primary hover:opacity-90 text-white"
        >
          <ShieldCheck size={18} className="mr-1" /> {saving ? "Saving…" : "Agree & continue"}
        </Button>
        <button data-testid="terms-signout" onClick={logout} className="w-full mt-3 text-xs font-bold text-ink-tertiary hover:text-ink">
          Not now — sign out
        </button>
      </div>
    </div>
  );
}
